import type { UpdateUserSettingsRequest, UserSettings } from "./models";

/** Type-level twin of `toSnakeCase`: `flagAgency` → `flag_agency`. */
type SnakeCase<S extends string> = S extends `${infer Head}${infer Tail}`
	? `${Head extends Lowercase<Head> ? Head : `_${Lowercase<Head>}`}${SnakeCase<Tail>}`
	: S;

/** Request keys whose snake_case form is not a cached `UserSettings` field, so they need a map entry. */
type RenamedRequestKey = {
	[Key in keyof UpdateUserSettingsRequest & string]-?: SnakeCase<Key> extends keyof UserSettings ? never : Key;
}[keyof UpdateUserSettingsRequest & string];

/** Instructions to the endpoint rather than settings — never written to the cache, so never mapped. */
type NonSettingRequestKey = "saveAsDefault";

/**
 * Request keys whose stored field is NOT their plain snake_case form — the backend stores them under a
 * `printout_` prefix (aliases on `UpdateUserSettingsRequest` in `backend/models/authn.py`). Without
 * this map the optimistic cache write lands on a field nothing reads, the cache keeps the old value,
 * and the printout panel snaps back to it until the server response arrives.
 *
 * Exhaustive by type: adding a request key whose snake_case form is not a `UserSettings` field fails
 * to compile until it is mapped here, and an entry for a key that snake-cases correctly is rejected.
 */
const CACHE_FIELD_BY_REQUEST_KEY = {
	spaceBetweenGroups: "printout_space_between_groups",
	includeTimeOffReason: "printout_include_time_off_reason",
	hideHppdNursingHours: "printout_hide_hppd_nursing_hours",
	flagNotOnboarded: "printout_flag_not_onboarded",
	flagAgency: "printout_flag_agency",
	mergeSimilarShiftTimes: "printout_merge_similar_shift_times",
	alignGroupTimesToPopular: "printout_align_group_times_to_popular",
	customTimeGroups: "printout_custom_time_groups",
	appendRoleToName: "printout_append_role_to_name",
	customColumnNames: "printout_custom_column_names",
	showDhppd: "printout_show_dhppd",
	showNhppd: "printout_show_nhppd",
	showActualStaffColumn: "printout_show_actual_staff_column",
	showActualHoursColumn: "printout_show_actual_hours_column",
	hppdIncludeTrainees: "printout_hppd_include_trainees",
} as const satisfies Record<Exclude<RenamedRequestKey, NonSettingRequestKey>, keyof UserSettings>;

const toSnakeCase = (key: string): string => key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

const isRenamedRequestKey = (key: string): key is keyof typeof CACHE_FIELD_BY_REQUEST_KEY => Object.hasOwn(CACHE_FIELD_BY_REQUEST_KEY, key);

/** The cached `UserSettings` field a request key is stored under. */
export const userSettingsCacheField = (requestKey: string): string =>
	isRenamedRequestKey(requestKey) ? CACHE_FIELD_BY_REQUEST_KEY[requestKey] : toSnakeCase(requestKey);

/**
 * The request's defined values keyed by their cached `UserSettings` field, for the optimistic write.
 * `saveAsDefault` is an instruction to the endpoint, not a setting, so it never reaches the cache.
 */
export const toOptimisticUserSettingsEntries = (request: UpdateUserSettingsRequest): Partial<UserSettings> =>
	Object.fromEntries(
		Object.entries(request)
			.filter(([key, value]) => value !== undefined && key !== "saveAsDefault")
			.map(([key, value]) => [userSettingsCacheField(key), value]),
	);
